import { useState } from 'react';
import {
  ActionRow,
  DesignerAction,
  DesignerBadge,
  DesignerDateRange,
  DesignerEntityCell,
  DesignerFilterBar,
  DesignerFilterControls,
  DesignerFilterSelect,
  DesignerPage,
  DesignerRow,
  DesignerRowAction,
  DesignerRowActions,
  DesignerSearch,
  DesignerTable,
  DesignerTd,
  DesignerTh,
  DesignerThead,
  EmptyState,
  ListCard,
  ListFoot,
  ListHead,
  ListScroll,
  LoadingState,
  MetricBand,
  MetricCard,
  MoneyCell,
  Pager,
} from '@tessma/design-system';
import { Refusal, formatDay, formatMoney, useAsync } from '../ui/async.js';
import { daysBetween, initialsOf, sumMinor, today } from './figures.js';
import {
  LIVE_BILL_STATUSES,
  LIVE_INVOICE_STATUSES,
  billStatusTone,
  billStatusWord,
  invoiceStatusTone,
  invoiceStatusWord,
} from './lifecycle.js';
import { AddPartySheet } from './inline-create.js';
import type { PartiesGateway } from '../parties/gateway.js';
import type {
  AgeingBucket,
  Bill,
  FinanceCustomer,
  FinanceGateway,
  FinancePaymentTerm,
  FinanceSupplier,
  Invoice,
} from './gateway.js';

/**
 * THE SENIOR DESIGNER'S PHASE 1 LIST SCREENS.
 *
 * Ported from their React source, bound to the reads and permissions that were
 * already here. Screens 02, 04, 08 and 10 share one composition in their pack
 * and share one here.
 *
 * WHY A SEPARATE FILE FROM `receivables-screens.tsx`. That file composes
 * Screens 03, 05, 06 and 07 from the `records/` anatomy they were accepted
 * with, and §20 forbids regressing them to integrate Phase 1. Replacing two of
 * its exports in place would have put the designer's anatomy and the Stitch
 * anatomy in one module with one set of imports, and the first careless reuse
 * would cross them.
 *
 * NOTHING HERE COMPUTES AN ACCOUNTING FACT. The sums are exact `BigInt`
 * additions of rows the server already decided this caller may read, which is
 * the pattern Screen 07's pivot established. No total is inferred, no status is
 * derived from a number, and no figure is rounded on the way to the screen.
 */

/** A formatted money cell, or a dash where the server holds no figure. */
function moneyOf(value: string, currency: string): string {
  return formatMoney(value, currency);
}

/**
 * Whether a minor-unit string is zero, whichever way the server spelt it.
 *
 * `'0'`, `'00'` and `'-0'` are the same amount, and a string comparison would
 * call two of them a figure worth showing.
 */
function isZeroMinor(value: string): boolean {
  try {
    return BigInt(value) === 0n;
  } catch {
    return false;
  }
}

/**
 * The initials tile's ground.
 *
 * The designer assigns one of eight per row by hand in their fixture. Derived
 * here from the name so the same customer is the same colour on every visit and
 * nothing has to be stored. Presentation only: it carries no meaning, which is
 * why it is a hash and not a status.
 */
const TILE_GROUNDS = [
  'var(--color-brand-teal)',
  'var(--color-info)',
  'var(--color-purple)',
  'var(--color-success)',
] as const;

function groundFor(name: string): string {
  let hash = 0;
  for (const character of name) hash = (hash + character.codePointAt(0)!) % 997;
  return TILE_GROUNDS[hash % TILE_GROUNDS.length] ?? TILE_GROUNDS[0];
}

/** The designer pages ten rows at a time. */
const ROWS_PER_PAGE = 10;

/* ===================================================================== */
/* Screen 02 — Customers list                                            */
/* ===================================================================== */

/**
 * SCREEN 02, from `src/app/finance/customers/page.tsx`.
 *
 * WHAT THE DESIGNER DREW AND WE DO NOT CARRY, each for a stated reason rather
 * than to save work:
 *
 *   `Type` (Corporate / SME / Government / Individual) — no such field exists.
 *   Finance holds terms, coding and credit standing; the organisation lives in
 *   the shared party record and has no segment.
 *
 *   `Account manager`, with its filter and its avatar — there is no
 *   account-manager concept anywhere in the product.
 *
 *   `Reference` (CUS-001) — `PartyIdentifier` carries a VAT number and a
 *   company number and nothing else. The party's id is a uuid, and no screen
 *   names a record by eight characters of one.
 *
 *   `Last invoice date` — derivable, but only by reading every invoice for
 *   every customer to fill one column. Deferred rather than faked.
 *
 *   The select-all checkbox column — there is no bulk endpoint behind it. A
 *   control that does nothing is worse than an absent one.
 *
 *   `Export` — their button raises `alert('Exporting customers list to
 *   CSV...')`. The export API is real but has no customers dataset, so the
 *   button is absent until it would do something.
 *
 * WHAT WE CARRY AND THE DESIGNER DID NOT DRAW: the credit limit and what the
 * server does when it is passed. Whether an account is on credit hold is the
 * one thing on this row that stops an invoice being issued, and §17 does not
 * let a visual change take it away.
 *
 * NO TREND AND NO SPARKLINE ON THESE FOUR CARDS. The designer draws a
 * percentage and a curve on each. `receivablesAgeing` is a position, not a
 * series: it has no previous period and no history, so there is nothing to
 * compare or plot. The dashboard's cards have both because `/dashboard`
 * returns `previousTotal` and `points`; these do not, so they say nothing
 * rather than something invented.
 */
export function CustomersScreen({
  gateway,
  parties,
  legalEntityId,
  canCreate = false,
  onOpen,
}: {
  readonly gateway: FinanceGateway;
  /**
   * Only the create sheet needs this. A list given none renders no New button,
   * which is the same rule every other action on these screens follows.
   */
  readonly parties?: PartiesGateway | undefined;
  readonly legalEntityId?: string | undefined;
  /** A courtesy. Adding a customer is two writes and the server guards both. */
  readonly canCreate?: boolean;
  /** Screen 02 → Screen 03. Absent, the name is text rather than a dead link. */
  readonly onOpen?: ((partyId: string) => void) | undefined;
}) {
  const { state, reload } = useAsync<{
    customers: readonly FinanceCustomer[];
    terms: readonly FinancePaymentTerm[];
    ageing: readonly AgeingBucket[];
  }>(
    async () => ({
      customers: await gateway.customers(legalEntityId),
      terms: legalEntityId === undefined ? [] : await gateway.paymentTerms(legalEntityId),
      ageing: await gateway.receivablesAgeing().catch(() => []),
    }),
    [gateway, legalEntityId],
  );

  const [query, setQuery] = useState('');
  const [standing, setStanding] = useState('all');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);

  if (state.status === 'loading') return <LoadingState title="Loading customers…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { customers, terms, ageing } = state.value;
  const currency = 'GBP';

  const termName = (id: string | null): string =>
    id === null ? '—' : (terms.find((term) => term.id === id)?.name ?? '—');

  const outstandingOf = (partyId: string): string =>
    sumMinor(ageing.filter((row) => row.partyId === partyId).map((row) => row.outstanding));
  /*
   * THE OVERDUE BANDS, NAMED RATHER THAN "NOT CURRENT". The server emits
   * `not_due` for debt on an invoice with no due date at all, and `current`
   * for debt not yet due. Taking everything that is not `current` would file
   * the first of those as overdue, which is an account chased for money that
   * is not late.
   */
  const overdueOf = (partyId: string): string =>
    sumMinor(
      ageing
        .filter((row) => row.partyId === partyId && row.bucket.startsWith('overdue'))
        .map((row) => row.outstanding),
    );

  const actions = (
    <ActionRow>
      {(query !== '' || standing !== 'all') && (
        <DesignerAction
          variant="ghost"
          icon="arrow-clockwise"
          onClick={() => {
            setQuery('');
            setStanding('all');
            setPage(1);
          }}
        >
          Clear filters
        </DesignerAction>
      )}
      {/*
        THE DESIGNER'S NEW CUSTOMER MODAL, AND IT IS THE REAL ONE. Theirs
        submits to `alert('New customer created successfully!')`. This opens
        `AddPartySheet`, which registers the organisation in the shared party
        record, grants it the customer role for this entity, and is refused by
        the server with its own candidates if the name already exists.
        It needs an entity to attach the Finance relationship to, so a caller
        reaching none is offered nothing rather than a form that cannot save.
      */}
      {canCreate && legalEntityId !== undefined && parties !== undefined ? (
        <DesignerAction
          variant="primary"
          icon="plus"
          onClick={() => {
            setAdding(true);
          }}
        >
          New customer
        </DesignerAction>
      ) : null}
    </ActionRow>
  );

  const sheet =
    canCreate && legalEntityId !== undefined && parties !== undefined ? (
      <AddPartySheet
        role="customer"
        open={adding}
        onClose={() => {
          setAdding(false);
        }}
        finance={gateway}
        parties={parties}
        legalEntityId={legalEntityId}
        onCreated={() => {
          setAdding(false);
          reload();
        }}
      />
    ) : null;

  if (customers.length === 0) {
    return (
      <DesignerPage>
        {actions}
        {sheet}
        <ListCard>
          <EmptyState
            icon="users-three"
            title="No customers"
            description="Customers within your reach appear here. A customer is an organisation in your shared records with a Finance relationship for this entity."
          />
        </ListCard>
      </DesignerPage>
    );
  }

  const active = customers.filter((customer) => customer.isActive && !customer.isOnHold);
  const total = sumMinor(ageing.map((row) => row.outstanding));
  const overdueRows = ageing.filter((row) => row.bucket.startsWith('overdue'));
  const overdueAccounts = new Set(overdueRows.map((row) => row.partyId)).size;

  const needle = query.trim().toLowerCase();
  const shown = customers.filter((customer) => {
    if (standing === 'active' && (!customer.isActive || customer.isOnHold)) return false;
    if (standing === 'hold' && !customer.isOnHold) return false;
    if (standing === 'inactive' && customer.isActive) return false;
    if (needle === '') return true;
    return [customer.displayName, customer.billingEmail ?? '']
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });

  const pages = Math.max(Math.ceil(shown.length / ROWS_PER_PAGE), 1);
  const current = Math.min(page, pages);
  const window = shown.slice((current - 1) * ROWS_PER_PAGE, current * ROWS_PER_PAGE);

  return (
    <DesignerPage>
      {actions}
      {sheet}

      {/*
        THE DESIGNER'S FOUR, WITH OUR FIGURES. Their Total Customers 156,
        Active 142, Overdue Accounts 18 and Total Receivables £480,000 are
        fixtures; these are the authorised relation, its active subset, the
        count of parties with anything past due, and the exact sum of the
        ageing rows the server sent.
      */}
      <MetricBand>
        <MetricCard
          icon="users-three"
          tone="teal"
          label="Total customers"
          value={String(customers.length)}
        />
        <MetricCard
          icon="user-circle"
          tone="success"
          label="Active customers"
          value={String(active.length)}
        />
        <MetricCard
          icon="warning"
          tone="danger"
          label="Overdue accounts"
          value={String(overdueAccounts)}
        />
        <MetricCard
          icon="coins"
          tone="info"
          label="Total receivables"
          value={moneyOf(total, currency)}
        />
      </MetricBand>

      <DesignerFilterBar>
        <DesignerFilterControls>
          <DesignerSearch
            label="Search customers"
            placeholder="Search customers by name or billing email…"
            value={query}
            onChange={(value) => {
              setQuery(value);
              setPage(1);
            }}
          />
          <DesignerFilterSelect
            label="Standing"
            value={standing}
            onChange={(value) => {
              setStanding(value);
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'All accounts' },
              { value: 'active', label: 'Active' },
              { value: 'hold', label: 'On credit hold' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
        </DesignerFilterControls>
      </DesignerFilterBar>

      <ListCard>
        <ListHead title="Customers" count={shown.length} />
        <ListScroll>
          <DesignerTable>
            <DesignerThead>
              <DesignerTh>Customer</DesignerTh>
              <DesignerTh>Primary contact</DesignerTh>
              <DesignerTh>Payment terms</DesignerTh>
              <DesignerTh align="right">Credit limit</DesignerTh>
              <DesignerTh align="right">Balance</DesignerTh>
              <DesignerTh align="right">Overdue</DesignerTh>
              <DesignerTh align="centre">Status</DesignerTh>
            </DesignerThead>
            <tbody>
              {window.map((customer) => (
                <DesignerRow key={`${customer.legalEntityId}:${customer.partyId}`}>
                  <DesignerTd>
                    {/*
                      A LINK ONLY WHERE THERE IS SOMEWHERE TO GO. The customer
                      page scopes its figures to a legal entity, and a caller
                      reaching none has no page to open.
                    */}
                    <DesignerEntityCell
                      initials={initialsOf(customer.displayName)}
                      name={customer.displayName}
                      meta={customer.billingCurrency ?? 'Entity currency'}
                      tone={groundFor(customer.displayName)}
                      {...(onOpen === undefined
                        ? {}
                        : {
                            onOpen: () => {
                              onOpen(customer.partyId);
                            },
                          })}
                    />
                  </DesignerTd>
                  <DesignerTd muted>{customer.billingEmail ?? '—'}</DesignerTd>
                  <DesignerTd>{termName(customer.paymentTermId)}</DesignerTd>
                  <MoneyCell
                    {...(customer.creditLimit === null
                      ? {}
                      : {
                          /*
                            WHAT THE LIMIT DOES, under the limit itself. A
                            number alone does not say whether passing it stops
                            an invoice or merely warns, and those are opposite
                            facts to somebody about to raise one. The designer
                            draws no such line; §17 does not let a visual
                            change drop it.
                          */
                          detail:
                            customer.creditPolicy === 'block'
                              ? 'Invoicing is blocked'
                              : 'A warning is shown',
                        })}
                  >
                    {/*
                      NO LIMIT IS NOT A LIMIT OF ZERO. A dash says the server
                      holds no figure; a 0 would say they may owe nothing.
                    */}
                    {customer.creditLimit === null
                      ? '—'
                      : formatMoney(customer.creditLimit, customer.billingCurrency ?? 'GBP')}
                  </MoneyCell>
                  <MoneyCell>{moneyOf(outstandingOf(customer.partyId), currency)}</MoneyCell>
                  <MoneyCell>{moneyOf(overdueOf(customer.partyId), currency)}</MoneyCell>
                  <DesignerTd align="centre">
                    {/*
                      THREE SERVER FLAGS, NOT A JUDGEMENT. On hold is the one
                      that stops an invoice being issued, so it is said first
                      and said in warning colour. Nothing here reads a balance
                      and calls an account overdue: that would be the browser
                      deciding a status from a number.
                    */}
                    {customer.isOnHold ? (
                      <DesignerBadge tone="warning" icon="lock-simple">
                        On credit hold
                      </DesignerBadge>
                    ) : customer.isActive ? (
                      <DesignerBadge tone="success">Active</DesignerBadge>
                    ) : (
                      <DesignerBadge tone="neutral">Inactive</DesignerBadge>
                    )}
                  </DesignerTd>
                </DesignerRow>
              ))}
            </tbody>
          </DesignerTable>
        </ListScroll>
        <ListFoot>
          <span>
            {shown.length === 0
              ? 'No customer matches these filters'
              : `Showing ${String(window.length)} of ${String(shown.length)}${
                  shown.length === customers.length
                    ? ''
                    : ` filtered from ${String(customers.length)}`
                }`}
          </span>
          <Pager
            page={current}
            pages={pages}
            onPage={(next) => {
              setPage(next);
            }}
          />
        </ListFoot>
      </ListCard>
    </DesignerPage>
  );
}

/* ===================================================================== */
/* Screen 04 — Sales invoices list                                       */
/* ===================================================================== */

/**
 * How many invoices one read of `/finance/invoices` can return.
 *
 * The gateway sends `limit=200` and the endpoint takes no offset, so this
 * screen holds the most recent 200 within the caller's reach and says so
 * rather than presenting its counts as the whole ledger.
 */
const REACH_LIMIT = 200;

/**
 * SCREEN 04, from `src/app/finance/sales-invoices/page.tsx`.
 *
 * WHAT THE DESIGNER DREW AND WE DO NOT CARRY:
 *
 *   `Outstanding (GBP)` — THE ONE THAT MATTERS, so here is the whole reason.
 *   An invoice carries no outstanding figure: what remains owed is its gross
 *   less every unreversed allocation against it, and that subtraction is done
 *   in SQL in two places (`ageingByParty` and `customerSummary`) and exposed
 *   per PARTY and per CUSTOMER, never per invoice. The three ways to fill this
 *   column were to add the derivation to a server read — backend scope this
 *   phase does not have — to divide the party's ageing across its invoices, or
 *   to infer the figure from the status in the browser. The second is a
 *   fabrication and the third is the browser deciding a money figure, which is
 *   the defect class §18 names. So the column is absent, as it already was on
 *   the implementation this replaces, and the gross total stands.
 *
 *   The select-all checkbox column, and the row's overflow menu — no bulk
 *   endpoint and no third row action exist behind either.
 *
 *   `Export` — their button raises `alert('Exporting sales invoices...')`.
 *
 *   Customer `tags` (Key Account, Government, New Customer, Standard) — there
 *   is no segment, tier or tag on a party anywhere in the product.
 *
 *   The `Amount range` filter — min and max are typed in major units and the
 *   totals are minor-unit strings, so filtering on them means the browser
 *   converting money by an exponent that is not the same in every currency.
 *   The endpoint takes no amount filter, so there is nothing to hand it either.
 *
 *   `Show 10/25/50 per page` — the endpoint takes a limit and no offset, so the
 *   page size on screen is a window over what one read returned. The pager
 *   moves within that window; a size control would imply it re-read the server.
 *
 * WHAT WE CARRY AND THE DESIGNER DID NOT DRAW: `Disputed`. It is a real status
 * on a real invoice, their five-status union has no room for it, and an invoice
 * held out of collections must not vanish from the list of invoices.
 *
 * `Sent` IS CALLED `Issued`, WHICH IS NOT THE SAME CLAIM. The designer's
 * second status says the document reached the customer. TESSMA records issue —
 * the number is allocated and the journal posted — separately from dispatch,
 * which lives in the send register with its own queued/sent/failed state. A
 * chip reading "Sent" over an invoice nobody emailed is the Collections defect
 * again in a different column, so the chip says what the server says.
 */
export function InvoicesScreen({
  gateway,
  legalEntityId,
  canCreate,
  canSend = false,
  onOpen,
  onNew,
  onViewDocument,
  onSend,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
  readonly canCreate: boolean;
  /** A courtesy. The send route and the server refuse the act again. */
  readonly canSend?: boolean;
  readonly onOpen: (invoiceId: string) => void;
  readonly onNew: () => void;
  readonly onViewDocument?: ((invoiceId: string) => void) | undefined;
  readonly onSend?: ((invoiceId: string) => void) | undefined;
}) {
  const { state, reload } = useAsync<{
    invoices: readonly Invoice[];
    names: ReadonlyMap<string, string>;
    ageing: readonly AgeingBucket[];
  }>(
    async () => ({
      invoices: await gateway.invoices(legalEntityId),
      // WHO THE INVOICE IS TO. An invoice carries a party id; the name is the
      // customer register's, read under the permission it already guards on. A
      // refusal costs the column, not the page.
      names: await gateway
        .customers(legalEntityId)
        .then((all) => new Map(all.map((one) => [one.partyId, one.displayName])))
        .catch(() => new Map<string, string>()),
      ageing: await gateway.receivablesAgeing().catch(() => []),
    }),
    [gateway, legalEntityId],
  );

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [customer, setCustomer] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  if (state.status === 'loading') return <LoadingState title="Loading invoices…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { invoices, names, ageing } = state.value;

  function clearFilters() {
    setQuery('');
    setStatus('all');
    setCustomer('all');
    setFrom('');
    setTo('');
    setPage(1);
  }

  const filtered =
    query !== '' || status !== 'all' || customer !== 'all' || from !== '' || to !== '';

  const actions = (
    <ActionRow>
      {filtered && (
        <DesignerAction variant="ghost" icon="arrow-clockwise" onClick={clearFilters}>
          Clear filters
        </DesignerAction>
      )}
      {canCreate && (
        <DesignerAction variant="primary" icon="plus" onClick={onNew}>
          New invoice
        </DesignerAction>
      )}
    </ActionRow>
  );

  if (invoices.length === 0) {
    return (
      <DesignerPage>
        {actions}
        <ListCard>
          <EmptyState
            icon="bank"
            title="No invoices"
            description="Invoices within your reach appear here."
          />
        </ListCard>
      </DesignerPage>
    );
  }

  const countOf = (which: string): number =>
    invoices.filter((invoice) => invoice.status === which).length;

  /*
   * PAST DUE IS A DERIVATION, AND THE SERVER DOES IT.
   *
   * `overdue` is not a status in this product — the migration says so in as
   * many words — it is the due date against today over what is still owed, and
   * the ageing endpoint is where that sum is computed. So the card counts the
   * documents the server put in an overdue band rather than this screen
   * reading due dates and deciding.
   *
   * ITS SCOPE IS WIDER THAN THE LIST'S, and that is worth knowing: the ageing
   * is scoped by what the caller may read, and the list is additionally scoped
   * to one legal entity. On a single-entity tenant they are the same set. This
   * is the arrangement every receivables screen here already uses.
   */
  const pastDueDocuments = ageing
    .filter((bucket) => bucket.bucket.startsWith('overdue'))
    .reduce((count, bucket) => count + bucket.documents, 0);

  /** True where the server's own dates say this one is late. Never a chip. */
  const isPastDue = (invoice: Invoice): boolean =>
    invoice.dueDate !== null &&
    LIVE_INVOICE_STATUSES.includes(invoice.status) &&
    invoice.dueDate < today();

  /*
   * WHAT THE FIVE COUNTS ARE OF, said on every card rather than on none.
   * Four of them count the window this read returned; a figure captioned
   * "of the 200 most recent" is a different claim from the same figure
   * presented as the ledger's total, and only one of them is true.
   */
  const basis =
    invoices.length >= REACH_LIMIT ? `Of the ${String(REACH_LIMIT)} most recent` : undefined;

  const metrics = (
    <MetricBand columns={5}>
      <MetricCard
        icon="file-text"
        tone="neutral"
        label="Draft invoices"
        value={String(countOf('draft'))}
        {...(basis === undefined ? {} : { caption: basis })}
      />
      <MetricCard
        icon="paper-plane-tilt"
        tone="info"
        label="Issued invoices"
        value={String(countOf('issued'))}
        {...(basis === undefined ? {} : { caption: basis })}
      />
      <MetricCard
        icon="warning"
        tone="danger"
        label="Past due invoices"
        value={String(pastDueDocuments)}
        caption="From the ageing, within your reach"
      />
      <MetricCard
        icon="clock"
        tone="warning"
        label="Part-paid invoices"
        value={String(countOf('part_paid'))}
        {...(basis === undefined ? {} : { caption: basis })}
      />
      <MetricCard
        icon="check-circle"
        tone="success"
        label="Paid invoices"
        value={String(countOf('paid'))}
        {...(basis === undefined ? {} : { caption: basis })}
      />
    </MetricBand>
  );

  /*
   * THE FILTERS OFFER WHAT THIS DATA HOLDS. The designer hard-codes five
   * statuses and ten customer names; every option here matches at least one
   * row, so no filter can be chosen that empties the list by construction.
   */
  const statusOptions = [
    { value: 'all', label: 'All statuses' },
    ...[...new Set(invoices.map((invoice) => invoice.status))]
      .sort()
      .map((one) => ({ value: one, label: invoiceStatusWord(one) })),
    // The designer's "Overdue" entry, named for what it is: a derivation over
    // the live statuses, not one of them.
    ...(invoices.some(isPastDue) ? [{ value: 'past_due', label: 'Past due' }] : []),
  ];

  const customerOptions = [
    { value: 'all', label: 'All customers' },
    ...[...new Set(invoices.map((invoice) => invoice.partyId))]
      .map((partyId) => ({
        value: partyId,
        label: names.get(partyId) ?? 'Not in your customer list',
      }))
      .sort((left, right) => left.label.localeCompare(right.label)),
  ];

  const needle = query.trim().toLowerCase();
  const shown = invoices.filter((invoice) => {
    if (status === 'past_due') {
      if (!isPastDue(invoice)) return false;
    } else if (status !== 'all' && invoice.status !== status) return false;
    if (customer !== 'all' && invoice.partyId !== customer) return false;
    // ISO days, so a string comparison is a date comparison.
    if (from !== '' && (invoice.issueDate === null || invoice.issueDate < from)) return false;
    if (to !== '' && (invoice.issueDate === null || invoice.issueDate > to)) return false;
    if (needle === '') return true;
    return [
      invoice.invoiceNumber ?? '',
      names.get(invoice.partyId) ?? '',
      invoice.customerReference ?? '',
    ]
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });

  const pages = Math.max(Math.ceil(shown.length / ROWS_PER_PAGE), 1);
  const current = Math.min(page, pages);
  const window = shown.slice((current - 1) * ROWS_PER_PAGE, current * ROWS_PER_PAGE);

  return (
    <DesignerPage>
      {actions}
      {metrics}

      <DesignerFilterBar>
        <DesignerFilterControls>
          <DesignerSearch
            label="Search invoices"
            placeholder="Search invoices, customer name or reference…"
            value={query}
            onChange={(value) => {
              setQuery(value);
              setPage(1);
            }}
          />
          <DesignerFilterSelect
            label="Status"
            value={status}
            options={statusOptions}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
          />
          <DesignerFilterSelect
            label="Customer"
            value={customer}
            options={customerOptions}
            onChange={(value) => {
              setCustomer(value);
              setPage(1);
            }}
          />
          <DesignerDateRange
            label="Issued between"
            fromLabel="Issued on or after"
            toLabel="Issued on or before"
            from={from}
            to={to}
            onFrom={(value) => {
              setFrom(value);
              setPage(1);
            }}
            onTo={(value) => {
              setTo(value);
              setPage(1);
            }}
          />
        </DesignerFilterControls>
      </DesignerFilterBar>

      <ListCard>
        <ListHead title="Invoices" count={shown.length} />
        <ListScroll>
          <DesignerTable>
            <DesignerThead>
              <DesignerTh>Invoice no</DesignerTh>
              <DesignerTh>Customer</DesignerTh>
              <DesignerTh>Issued</DesignerTh>
              <DesignerTh>Due</DesignerTh>
              {/*
                NO CURRENCY IN THE HEADING. The designer writes "Gross (GBP)",
                which is true of their fixture and not of this list: an invoice
                carries its own currency and two rows can differ. The code is in
                the cell, where it belongs to the amount it qualifies.
              */}
              <DesignerTh align="right">Gross</DesignerTh>
              <DesignerTh align="centre">Status</DesignerTh>
              <DesignerTh align="centre">Actions</DesignerTh>
            </DesignerThead>
            <tbody>
              {window.map((invoice) => {
                const name = names.get(invoice.partyId);
                const late =
                  invoice.dueDate === null ? null : daysBetween(invoice.dueDate, today());
                return (
                  <DesignerRow key={invoice.id}>
                    <DesignerTd>
                      {/*
                        THE DESIGNER'S INVOICE CELL: the number as a link over
                        its reference. A draft has no number yet — the server
                        allocates it at issue — so it is named for what it is
                        rather than given a placeholder that looks like one.
                      */}
                      <DesignerEntityCell
                        name={invoice.invoiceNumber ?? 'Draft'}
                        {...(invoice.customerReference === null
                          ? {}
                          : { meta: invoice.customerReference })}
                        onOpen={() => {
                          onOpen(invoice.id);
                        }}
                      />
                    </DesignerTd>
                    {name === undefined ? (
                      <DesignerTd muted>Not in your customer list</DesignerTd>
                    ) : (
                      /* The designer draws the customer as a bold name beside
                         its tags. There are no tags, so it is the name. */
                      <DesignerTd>
                        <DesignerEntityCell name={name} />
                      </DesignerTd>
                    )}
                    <DesignerTd muted>{formatDay(invoice.issueDate)}</DesignerTd>
                    {/*
                      THE DESIGNER'S RED SUB-LINE UNDER THE DUE DATE, ON THE
                      ROWS IT IS TRUE OF. Theirs reads "(30 days)" on every row
                      in the same red, which says "late" about an invoice that
                      is not. This counts days between two dates the server
                      sent, shows it only where the due date has passed on a
                      live invoice, and decides nothing: the status chip beside
                      it is the server's word and this never contradicts it.
                    */}
                    <DesignerTd
                      {...(late !== null &&
                      late > 0 &&
                      LIVE_INVOICE_STATUSES.includes(invoice.status)
                        ? { detail: `${String(late)} days past due` }
                        : {})}
                    >
                      {formatDay(invoice.dueDate)}
                    </DesignerTd>
                    <MoneyCell>{moneyOf(invoice.grossTotal, invoice.currency)}</MoneyCell>
                    <DesignerTd align="centre">
                      <DesignerBadge tone={invoiceStatusTone(invoice.status)}>
                        {invoiceStatusWord(invoice.status)}
                      </DesignerBadge>
                    </DesignerTd>
                    <DesignerTd align="centre">
                      <DesignerRowActions>
                        {onViewDocument !== undefined && (
                          <DesignerRowAction
                            icon="file-text"
                            label={`View the document for ${invoice.invoiceNumber ?? 'this draft'}`}
                            onClick={() => {
                              onViewDocument(invoice.id);
                            }}
                          />
                        )}
                        {canSend && onSend !== undefined && invoice.invoiceNumber !== null && (
                          <DesignerRowAction
                            icon="paper-plane-tilt"
                            label={`Send ${invoice.invoiceNumber} to the customer`}
                            onClick={() => {
                              onSend(invoice.id);
                            }}
                          />
                        )}
                      </DesignerRowActions>
                    </DesignerTd>
                  </DesignerRow>
                );
              })}
            </tbody>
          </DesignerTable>
        </ListScroll>
        <ListFoot>
          <span>
            {shown.length === 0
              ? 'No invoice matches these filters'
              : `Showing ${String(window.length)} of ${String(shown.length)}${
                  shown.length === invoices.length
                    ? ''
                    : ` filtered from ${String(invoices.length)}`
                } within your reach`}
          </span>
          <Pager
            page={current}
            pages={pages}
            onPage={(next) => {
              setPage(next);
            }}
          />
        </ListFoot>
      </ListCard>
    </DesignerPage>
  );
}

/* ===================================================================== */
/* Screen 08 — Suppliers list                                            */
/* ===================================================================== */

/**
 * SCREEN 08, from `src/app/finance/suppliers/page.tsx`.
 *
 * THE SAME COMPOSITION AS SCREEN 02, WHICH IS THE DESIGNER'S OWN DOING: four
 * cards, a filter row, a register, a pager. What differs is what Finance holds
 * about a supplier, and it is less than it holds about a customer.
 *
 * WHAT THE DESIGNER DREW AND WE DO NOT CARRY:
 *
 *   `Bank status` — Verified / Pending / Not Verified. THERE IS NO SUCH
 *   WORKFLOW. A supplier record carries `bankAccountLast4` and nothing else:
 *   no verification state, no who-checked-it, no when. A green "Verified" chip
 *   beside a payee is a control somebody would rely on before releasing money,
 *   and this product has not performed it. The column therefore says what the
 *   server does know — the last four digits it holds, or that it holds none —
 *   which is also the only form a bank detail is shown in anywhere here.
 *
 *   `Category` (Materials, Logistics, Services…) and its filter — no category,
 *   segment or tier exists on a party.
 *
 *   `Primary contact`, name and email — `FinanceCustomer` carries a billing
 *   email; `FinanceSupplier` carries none. Nothing to bind.
 *
 *   `Supplier code` (SUP-014) under the name — there is no supplier code. The
 *   party's id is a uuid and no screen names a record by part of one.
 *
 *   `Last bill` — the same decision Screen 02 took for "Last invoice date",
 *   for a sharper reason: the bills within one read are the most recent, so a
 *   dash would mean "never billed" on one row and "not in this window" on
 *   another. Two meanings in one cell is worse than an absent column.
 *
 *   `Spend band` — a money filter the browser would have to band itself, over
 *   figures the endpoint cannot filter on.
 *
 *   The select-all column, the row overflow menu, and `Export` — no bulk
 *   endpoint, no third action, and their button raises
 *   `alert('Exporting suppliers...')`.
 */
export function SuppliersScreen({
  gateway,
  parties,
  legalEntityId,
  canCreate = false,
}: {
  readonly gateway: FinanceGateway;
  readonly parties?: PartiesGateway | undefined;
  readonly legalEntityId?: string | undefined;
  /** A courtesy. Adding a supplier is two writes and the server guards both. */
  readonly canCreate?: boolean;
}) {
  const { state, reload } = useAsync<{
    suppliers: readonly FinanceSupplier[];
    terms: readonly FinancePaymentTerm[];
    ageing: readonly AgeingBucket[];
  }>(
    async () => ({
      suppliers: await gateway.suppliers(legalEntityId),
      terms: legalEntityId === undefined ? [] : await gateway.paymentTerms(legalEntityId),
      ageing: await gateway.payablesAgeing().catch(() => []),
    }),
    [gateway, legalEntityId],
  );

  const [query, setQuery] = useState('');
  const [standing, setStanding] = useState('all');
  const [term, setTerm] = useState('all');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);

  if (state.status === 'loading') return <LoadingState title="Loading suppliers…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { suppliers, terms, ageing } = state.value;
  const currency = 'GBP';

  const termName = (id: string | null): string =>
    id === null ? '—' : (terms.find((one) => one.id === id)?.name ?? '—');

  const owedTo = (partyId: string): string =>
    sumMinor(ageing.filter((row) => row.partyId === partyId).map((row) => row.outstanding));
  const overdueTo = (partyId: string): string =>
    sumMinor(
      ageing
        .filter((row) => row.partyId === partyId && row.bucket.startsWith('overdue'))
        .map((row) => row.outstanding),
    );

  const mayAdd = canCreate && legalEntityId !== undefined && parties !== undefined;

  const actions = (
    <ActionRow>
      {(query !== '' || standing !== 'all' || term !== 'all') && (
        <DesignerAction
          variant="ghost"
          icon="arrow-clockwise"
          onClick={() => {
            setQuery('');
            setStanding('all');
            setTerm('all');
            setPage(1);
          }}
        >
          Clear filters
        </DesignerAction>
      )}
      {/*
        THEIRS SUBMITS TO `alert('Supplier created successfully!')`. This is the
        same sheet the bill entry screen uses: the organisation goes into the
        shared party record, the Finance relationship onto this entity, and the
        server refuses a duplicate with its own candidates.
      */}
      {mayAdd ? (
        <DesignerAction
          variant="primary"
          icon="plus"
          onClick={() => {
            setAdding(true);
          }}
        >
          New supplier
        </DesignerAction>
      ) : null}
    </ActionRow>
  );

  const sheet =
    mayAdd && legalEntityId !== undefined && parties !== undefined ? (
      <AddPartySheet
        role="supplier"
        open={adding}
        onClose={() => {
          setAdding(false);
        }}
        finance={gateway}
        parties={parties}
        legalEntityId={legalEntityId}
        onCreated={() => {
          setAdding(false);
          reload();
        }}
      />
    ) : null;

  if (suppliers.length === 0) {
    return (
      <DesignerPage>
        {actions}
        {sheet}
        <ListCard>
          <EmptyState
            icon="users-three"
            title="No suppliers"
            description="Suppliers within your reach appear here. A supplier is an organisation in your shared records with a Finance relationship for this entity."
          />
        </ListCard>
      </DesignerPage>
    );
  }

  const active = suppliers.filter((supplier) => supplier.isActive);
  const owed = sumMinor(ageing.map((row) => row.outstanding));
  const overdueBills = ageing
    .filter((row) => row.bucket.startsWith('overdue'))
    .reduce((count, row) => count + row.documents, 0);

  const termOptions = [
    { value: 'all', label: 'All terms' },
    ...[...new Set(suppliers.map((supplier) => supplier.paymentTermId))]
      .filter((id): id is string => id !== null)
      .map((id) => ({ value: id, label: termName(id) }))
      .sort((left, right) => left.label.localeCompare(right.label)),
  ];

  const needle = query.trim().toLowerCase();
  const shown = suppliers.filter((supplier) => {
    if (standing === 'active' && !supplier.isActive) return false;
    if (standing === 'inactive' && supplier.isActive) return false;
    if (term !== 'all' && supplier.paymentTermId !== term) return false;
    if (needle === '') return true;
    return supplier.displayName.toLowerCase().includes(needle);
  });

  const pages = Math.max(Math.ceil(shown.length / ROWS_PER_PAGE), 1);
  const current = Math.min(page, pages);
  const window = shown.slice((current - 1) * ROWS_PER_PAGE, current * ROWS_PER_PAGE);

  return (
    <DesignerPage>
      {actions}
      {sheet}

      {/*
        THE DESIGNER'S FOUR, WITH OUR FIGURES. Theirs read 48, 42, 7 and
        £128,400. These are the authorised relation, its active subset, the
        documents the server put in an overdue band, and the exact sum of the
        payables ageing rows it sent.
      */}
      <MetricBand>
        <MetricCard
          icon="buildings"
          tone="teal"
          label="Total suppliers"
          value={String(suppliers.length)}
        />
        <MetricCard
          icon="check-circle"
          tone="success"
          label="Active suppliers"
          value={String(active.length)}
        />
        <MetricCard
          icon="warning"
          tone="danger"
          label="Overdue bills"
          value={String(overdueBills)}
          caption="From the ageing, within your reach"
        />
        <MetricCard
          icon="coins"
          tone="info"
          label="Outstanding payables"
          value={moneyOf(owed, currency)}
        />
      </MetricBand>

      <DesignerFilterBar>
        <DesignerFilterControls>
          <DesignerSearch
            label="Search suppliers"
            placeholder="Search suppliers by name…"
            value={query}
            onChange={(value) => {
              setQuery(value);
              setPage(1);
            }}
          />
          <DesignerFilterSelect
            label="Status"
            value={standing}
            onChange={(value) => {
              setStanding(value);
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'All suppliers' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
          <DesignerFilterSelect
            label="Payment terms"
            value={term}
            options={termOptions}
            onChange={(value) => {
              setTerm(value);
              setPage(1);
            }}
          />
        </DesignerFilterControls>
      </DesignerFilterBar>

      <ListCard>
        <ListHead title="Suppliers" count={shown.length} />
        <ListScroll>
          <DesignerTable>
            <DesignerThead>
              <DesignerTh>Supplier</DesignerTh>
              <DesignerTh align="right">Current balance</DesignerTh>
              <DesignerTh align="right">Overdue amount</DesignerTh>
              <DesignerTh>Payment terms</DesignerTh>
              <DesignerTh>Bank account</DesignerTh>
              <DesignerTh align="centre">Status</DesignerTh>
            </DesignerThead>
            <tbody>
              {window.map((supplier) => (
                <DesignerRow key={`${supplier.legalEntityId}:${supplier.partyId}`}>
                  <DesignerTd>
                    {/*
                      THE DESIGNER'S TILE, WITHOUT THEIR PER-ROW ICON. They
                      assign a glyph per supplier by hand — a leaf, a truck, a
                      shield — from the category that does not exist here. The
                      initials tile is the same shape, carries no claim, and is
                      the same one Screen 02 draws.
                    */}
                    <DesignerEntityCell
                      initials={initialsOf(supplier.displayName)}
                      name={supplier.displayName}
                      tone={groundFor(supplier.displayName)}
                      {...(supplier.defaultTaxCode === null
                        ? {}
                        : { meta: `Tax code ${supplier.defaultTaxCode}` })}
                    />
                  </DesignerTd>
                  <MoneyCell>{moneyOf(owedTo(supplier.partyId), currency)}</MoneyCell>
                  <MoneyCell>{moneyOf(overdueTo(supplier.partyId), currency)}</MoneyCell>
                  <DesignerTd>{termName(supplier.paymentTermId)}</DesignerTd>
                  <DesignerTd muted>
                    {/*
                      FOUR DIGITS OR NOTHING, AND NEVER A VERIFICATION CLAIM.
                      The designer's chip reads Verified / Pending / Not
                      Verified; no such state is recorded anywhere, and a green
                      tick beside a payee is exactly the sort of assurance
                      somebody releases money on. "Not held" says the server
                      holds no detail, which is a different fact from a detail
                      nobody has checked.
                    */}
                    {supplier.bankAccountLast4 === null
                      ? 'Not held'
                      : `Ending ${supplier.bankAccountLast4}`}
                  </DesignerTd>
                  <DesignerTd align="centre">
                    {supplier.isActive ? (
                      <DesignerBadge tone="success">Active</DesignerBadge>
                    ) : (
                      <DesignerBadge tone="neutral">Inactive</DesignerBadge>
                    )}
                  </DesignerTd>
                </DesignerRow>
              ))}
            </tbody>
          </DesignerTable>
        </ListScroll>
        <ListFoot>
          <span>
            {shown.length === 0
              ? 'No supplier matches these filters'
              : `Showing ${String(window.length)} of ${String(shown.length)}${
                  shown.length === suppliers.length
                    ? ''
                    : ` filtered from ${String(suppliers.length)}`
                }`}
          </span>
          <Pager
            page={current}
            pages={pages}
            onPage={(next) => {
              setPage(next);
            }}
          />
        </ListFoot>
      </ListCard>
    </DesignerPage>
  );
}

/* ===================================================================== */
/* Screen 10 — Purchase invoices / supplier bills                        */
/* ===================================================================== */

/** The designer's "Due this week" window. */
const DUE_SOON_DAYS = 7;

/**
 * SCREEN 10, from `src/app/finance/purchase-invoices/page.tsx`.
 *
 * ONE STATUS COLUMN, WHERE THE DESIGNER DRAWS TWO. Theirs are `Approval
 * status` — Pending, Approved, Rejected — and `Payment status` — Not paid,
 * Part-paid, Paid. A bill here holds ONE lifecycle: draft, pending_approval,
 * approved, posted, part_paid, paid, disputed, void. Splitting it across two
 * columns means the browser asserting two states where the server records one,
 * and it cannot be done without inventing: a posted bill would have to be
 * called "Approved" in a column nothing approved it into, `disputed` and `void`
 * fit neither column, and `Rejected` IS NOT A STATE THIS PRODUCT HAS — a bill
 * that fails approval goes back to the enterer, it is not stamped rejected. So
 * the register shows the server's own word, once, as Screen 04 does.
 *
 * SUBMIT, APPROVE AND POST STAY ON THE BILL. The designer draws a tick and a
 * cross on every row. Each of those is a server decision with its own
 * permission (`finance.bill.create`, `.approve`, `.post`), a separation-of-duty
 * rule behind it, and in the case of posting a journal at the end of it. They
 * belong on the bill, where the thing being approved can be read, and that is
 * where this screen's row action leads.
 *
 * WHAT ELSE THE DESIGNER DREW AND WE DO NOT CARRY:
 *
 *   `Outstanding (GBP)` — the same absence as Screen 04 and for the same
 *   reason: what remains owed on a bill is its gross less every unreversed
 *   allocation, computed in SQL and exposed per party, never per document.
 *
 *   The bill's `description` under its reference — a bill's description lives
 *   on its lines, and the list read returns no lines. The supplier's name goes
 *   there instead, which is what a reader of a reference needs first.
 *
 *   `Supplier code`, the select-all column, the row overflow menu, the amount
 *   range filter, and `Export` — as Screens 04 and 08: no such field, no bulk
 *   endpoint, no third action, no browser-side money filter, and their button
 *   raises `alert('Exporting bills...')`.
 */
export function BillsScreen({
  gateway,
  legalEntityId,
  canEnter,
  onOpen,
  onNew,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
  readonly canEnter: boolean;
  readonly onOpen: (billId: string) => void;
  readonly onNew: () => void;
}) {
  const { state, reload } = useAsync<{
    bills: readonly Bill[];
    names: ReadonlyMap<string, string>;
    ageing: readonly AgeingBucket[];
  }>(
    async () => ({
      bills: await gateway.bills(legalEntityId),
      names: await gateway
        .suppliers(legalEntityId)
        .then((all) => new Map(all.map((one) => [one.partyId, one.displayName])))
        .catch(() => new Map<string, string>()),
      ageing: await gateway.payablesAgeing().catch(() => []),
    }),
    [gateway, legalEntityId],
  );

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [supplier, setSupplier] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  if (state.status === 'loading') return <LoadingState title="Loading bills…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const { bills, names, ageing } = state.value;

  const filtered =
    query !== '' || status !== 'all' || supplier !== 'all' || from !== '' || to !== '';

  const actions = (
    <ActionRow>
      {filtered && (
        <DesignerAction
          variant="ghost"
          icon="arrow-clockwise"
          onClick={() => {
            setQuery('');
            setStatus('all');
            setSupplier('all');
            setFrom('');
            setTo('');
            setPage(1);
          }}
        >
          Clear filters
        </DesignerAction>
      )}
      {canEnter && (
        <DesignerAction variant="primary" icon="plus" onClick={onNew}>
          Enter a bill
        </DesignerAction>
      )}
    </ActionRow>
  );

  if (bills.length === 0) {
    return (
      <DesignerPage>
        {actions}
        <ListCard>
          <EmptyState
            icon="receipt"
            title="No bills"
            description="Bills within your reach appear here."
          />
        </ListCard>
      </DesignerPage>
    );
  }

  const countOf = (which: string): number => bills.filter((bill) => bill.status === which).length;

  /** Days until this bill falls due, or null where it has no due date. */
  const dueIn = (bill: Bill): number | null =>
    bill.dueDate === null ? null : daysBetween(today(), bill.dueDate);

  const isDueSoon = (bill: Bill): boolean => {
    const days = dueIn(bill);
    return (
      days !== null &&
      days >= 0 &&
      days <= DUE_SOON_DAYS &&
      LIVE_BILL_STATUSES.includes(bill.status)
    );
  };

  const isPastDue = (bill: Bill): boolean => {
    const days = dueIn(bill);
    return days !== null && days < 0 && LIVE_BILL_STATUSES.includes(bill.status);
  };

  /*
   * PAST DUE FROM THE SERVER'S OWN BANDS, as on Screen 04. "Due this week" has
   * no equivalent — `current` means not yet due and the ageing bands it no
   * further — so that card counts bills whose SERVER-SENT due date falls in the
   * next seven days. It is a date predicate over rows the server chose, not a
   * figure this screen computed, and its caption says which seven days.
   */
  const pastDueDocuments = ageing
    .filter((bucket) => bucket.bucket.startsWith('overdue'))
    .reduce((count, bucket) => count + bucket.documents, 0);

  const basis =
    bills.length >= REACH_LIMIT ? `Of the ${String(REACH_LIMIT)} most recent` : undefined;

  const metrics = (
    <MetricBand>
      <MetricCard
        icon="hourglass-medium"
        tone="warning"
        label="Awaiting approval"
        value={String(countOf('pending_approval'))}
        {...(basis === undefined ? {} : { caption: basis })}
      />
      <MetricCard
        icon="calendar-blank"
        tone="info"
        label="Due this week"
        value={String(bills.filter(isDueSoon).length)}
        caption={`Falling due within ${String(DUE_SOON_DAYS)} days`}
      />
      <MetricCard
        icon="warning"
        tone="danger"
        label="Past due"
        value={String(pastDueDocuments)}
        caption="From the ageing, within your reach"
      />
      <MetricCard
        icon="check-circle"
        tone="success"
        label="Paid"
        value={String(countOf('paid'))}
        {...(basis === undefined ? {} : { caption: basis })}
      />
    </MetricBand>
  );

  const statusOptions = [
    { value: 'all', label: 'All statuses' },
    ...[...new Set(bills.map((bill) => bill.status))]
      .sort()
      .map((one) => ({ value: one, label: billStatusWord(one) })),
    ...(bills.some(isPastDue) ? [{ value: 'past_due', label: 'Past due' }] : []),
  ];

  const supplierOptions = [
    { value: 'all', label: 'All suppliers' },
    ...[...new Set(bills.map((bill) => bill.partyId))]
      .map((partyId) => ({
        value: partyId,
        label: names.get(partyId) ?? 'Not in your supplier list',
      }))
      .sort((left, right) => left.label.localeCompare(right.label)),
  ];

  const needle = query.trim().toLowerCase();
  const shown = bills.filter((bill) => {
    if (status === 'past_due') {
      if (!isPastDue(bill)) return false;
    } else if (status !== 'all' && bill.status !== status) return false;
    if (supplier !== 'all' && bill.partyId !== supplier) return false;
    if (from !== '' && bill.billDate < from) return false;
    if (to !== '' && bill.billDate > to) return false;
    if (needle === '') return true;
    return [bill.supplierReference, names.get(bill.partyId) ?? '']
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });

  const pages = Math.max(Math.ceil(shown.length / ROWS_PER_PAGE), 1);
  const current = Math.min(page, pages);
  const window = shown.slice((current - 1) * ROWS_PER_PAGE, current * ROWS_PER_PAGE);

  return (
    <DesignerPage>
      {actions}
      {metrics}

      <DesignerFilterBar>
        <DesignerFilterControls>
          <DesignerSearch
            label="Search bills"
            placeholder="Search bills by reference or supplier…"
            value={query}
            onChange={(value) => {
              setQuery(value);
              setPage(1);
            }}
          />
          <DesignerFilterSelect
            label="Status"
            value={status}
            options={statusOptions}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
          />
          <DesignerFilterSelect
            label="Supplier"
            value={supplier}
            options={supplierOptions}
            onChange={(value) => {
              setSupplier(value);
              setPage(1);
            }}
          />
          <DesignerDateRange
            label="Billed between"
            fromLabel="Billed on or after"
            toLabel="Billed on or before"
            from={from}
            to={to}
            onFrom={(value) => {
              setFrom(value);
              setPage(1);
            }}
            onTo={(value) => {
              setTo(value);
              setPage(1);
            }}
          />
        </DesignerFilterControls>
      </DesignerFilterBar>

      <ListCard>
        <ListHead title="Supplier bills" count={shown.length} />
        <ListScroll>
          <DesignerTable>
            <DesignerThead>
              <DesignerTh>Bill reference</DesignerTh>
              <DesignerTh>Supplier</DesignerTh>
              <DesignerTh>Bill date</DesignerTh>
              <DesignerTh>Due date</DesignerTh>
              <DesignerTh align="right">Gross</DesignerTh>
              <DesignerTh align="centre">Status</DesignerTh>
              <DesignerTh align="centre">Actions</DesignerTh>
            </DesignerThead>
            <tbody>
              {window.map((bill) => {
                const name = names.get(bill.partyId);
                const days = dueIn(bill);
                return (
                  <DesignerRow key={bill.id}>
                    <DesignerTd>
                      {/*
                        THEIR REFERENCE, WITHOUT THE DESCRIPTION THE DESIGNER
                        PUTS UNDER IT. A bill's description lives on its lines,
                        and the list read returns no lines.
                      */}
                      <DesignerEntityCell
                        name={bill.supplierReference}
                        onOpen={() => {
                          onOpen(bill.id);
                        }}
                      />
                    </DesignerTd>
                    {name === undefined ? (
                      <DesignerTd muted>Not in your supplier list</DesignerTd>
                    ) : (
                      <DesignerTd>
                        <DesignerEntityCell name={name} />
                      </DesignerTd>
                    )}
                    <DesignerTd muted>{formatDay(bill.billDate)}</DesignerTd>
                    <DesignerTd
                      {...(days !== null && days < 0 && LIVE_BILL_STATUSES.includes(bill.status)
                        ? { detail: `${String(-days)} days past due` }
                        : {})}
                    >
                      {formatDay(bill.dueDate)}
                    </DesignerTd>
                    <MoneyCell
                      {...(isZeroMinor(bill.reverseChargeTax)
                        ? {}
                        : {
                            /*
                              FR-VAT-008. The reverse-charge VAT is NOT in the
                              gross — it is what this purchase makes us liable
                              to account for — so a gross figure shown without
                              it understates the obligation to anybody reading
                              the row for what this bill costs.
                            */
                            detail: `Reverse charge ${formatMoney(
                              bill.reverseChargeTax,
                              bill.currency,
                            )}`,
                          })}
                    >
                      {moneyOf(bill.grossTotal, bill.currency)}
                    </MoneyCell>
                    <DesignerTd align="centre">
                      <DesignerBadge tone={billStatusTone(bill.status)}>
                        {billStatusWord(bill.status)}
                      </DesignerBadge>
                    </DesignerTd>
                    <DesignerTd align="centre">
                      <DesignerRowActions>
                        {/*
                          ONE ACTION, AND IT GOES TO THE BILL. Approving is a
                          server decision with its own permission and a
                          separation-of-duty rule; it is not a tick in a list.
                        */}
                        <DesignerRowAction
                          icon="eye"
                          label={`Open ${bill.supplierReference}`}
                          onClick={() => {
                            onOpen(bill.id);
                          }}
                        />
                      </DesignerRowActions>
                    </DesignerTd>
                  </DesignerRow>
                );
              })}
            </tbody>
          </DesignerTable>
        </ListScroll>
        <ListFoot>
          <span>
            {shown.length === 0
              ? 'No bill matches these filters'
              : `Showing ${String(window.length)} of ${String(shown.length)}${
                  shown.length === bills.length ? '' : ` filtered from ${String(bills.length)}`
                } within your reach`}
          </span>
          <Pager
            page={current}
            pages={pages}
            onPage={(next) => {
              setPage(next);
            }}
          />
        </ListFoot>
      </ListCard>
    </DesignerPage>
  );
}
